import queryString, { InputParams } from 'query-string';

import { PaginationI } from '~src/interfaces/pagination.interface';
import { SessionUserI } from '~src/interfaces/session.types';
import {
  fetchCompanyAnswersList,
  fetchCompanyNewsList,
  fetchCompanyQuestionsImproveList,
  fetchCompanyQuestionsList,
  fetchCompanyTopicsList,
} from '~src/modules/reports/companyProfile/actions/companyProfileActions';
import {
  fetchGroupAnswersList,
  fetchGroupNewsList,
  fetchGroupQuestionsImproveList,
  fetchGroupQuestionsList,
  fetchGroupTopicsList,
} from '~src/modules/reports/groupProfile/actions/groupProfileActions';
import { GroupProfileState } from '~src/modules/reports/groupProfile/reducers/groupProfileReducer';
import { AnswerObj, AnswerResponse } from '~src/modules/reports/interfaces/answer.interface';
import {
  ProfileTypes,
  ReportEntityTypes,
  ReportSubjectTypes,
  ReportsObj,
} from '~src/modules/reports/interfaces/header.interface';
import { ImproveResponse } from '~src/modules/reports/interfaces/improve.interface';
import { NewsResponse } from '~src/modules/reports/interfaces/news.interface';
import { Note, NotesResponse } from '~src/modules/reports/interfaces/notes.interface';
import { ParticipantsResponse } from '~src/modules/reports/interfaces/participants.interface';
import { QuestionResponse } from '~src/modules/reports/interfaces/questions.interface';
import { ProfileSurvey, TopicsResponse } from '~src/modules/reports/interfaces/topics.interface';
import {
  getSurveyNews,
  getSurveyParticipantsList,
  getSurveyQuestionAnswers,
  getSurveyQuestionsList,
  getSurveyTopics,
} from '~src/modules/reports/surveyProfile/actions/surveyProfile.actions';
import { SurveyProfileState } from '~src/modules/reports/surveyProfile/reducers/surveyProfile.reducer';
import {
  fetchUserAnswersList,
  fetchUserNewsList,
  fetchUserQuestionsImproveList,
  fetchUserQuestionsList,
  fetchUserTopicsList,
} from '~src/modules/reports/userProfile/actions/userProfileActions';
import { UserProfileState } from '~src/modules/reports/userProfile/reducers/userProfileReducer';
import { StoreI } from '~src/rootReducer';
import { ApiReduxResp } from '~src/services/apiMiddleware';
import { translateContent } from '~src/services/translationServices';
import { SortingOption } from '~src/shared/sortSelector/SortSelector';
import { getUrlFromObject, mapIdToData } from '~src/utils';

/*******************************************************************************
 *  DISTRIBUTERS FOR FETCHING THE DATA  *
 *******************************************************************************/

export function fetchTopicListOfSubject(
  subjectType: ReportEntityTypes,
  filterObject: { [key: string]: string | null },
  subject: ReportsObj,
  me: SessionUserI,
  showSelfAssessments: 'self' | 'no_self',
) {
  const queryStr = getUrlFromObject({ ...filterObject, showSelfAssessments });
  switch (subjectType) {
    case ReportEntityTypes.Company:
      return fetchCompanyTopicsList(queryStr);

    case ReportEntityTypes.User:
      return fetchUserTopicsList(queryStr, subject.id);

    case ReportEntityTypes.Group:
      return fetchGroupTopicsList(queryStr, subject.id);

    case ReportEntityTypes.Survey:
      return getSurveyTopics(queryStr, subject, me);

    default:
      return null;
  }
}

export type FetchNoteListOfSubjectType = (
  subjectId: string,
  sortings: { [key: string]: string | null },
) => ApiReduxResp<NotesResponse>;

export type FetchNote = (noteId: string, includes: string[]) => ApiReduxResp<Note>;

export type FetchParticipantsListOfSubjectType = (
  type: ReportEntityTypes,
  filterObject: { [key: string]: string | null },
  subject: ReportsObj,
  me: SessionUserI | null,
) => ApiReduxResp<ParticipantsResponse>;

export const fetchParticipantsListOfSubject = (
  subjectType: ReportEntityTypes,
  filterObject: { [key: string]: string | null },
  subject: ReportsObj,
  sessionUser: SessionUserI | null,
) => {
  if (filterObject.page) {
    delete filterObject.page;
  }
  const queryStr = getUrlFromObject(filterObject);
  switch (subjectType) {
    case ReportEntityTypes.Survey: {
      return getSurveyParticipantsList(subject.id, subject, sessionUser, queryStr);
    }
    default:
      return null;
  }
};

export type FetchQuestionsImproveListOfSubjectType = (
  type: ReportEntityTypes,
  filterObject: { [key: string]: string | null },
  topicId: string,
  subjectId: string,
  showSelfAssessments: 'self' | 'no_self',
) => ApiReduxResp<ImproveResponse>;

export function fetchQuestionsImproveListOfSubject(
  type: ReportEntityTypes,
  filterObject: { [key: string]: string | null },
  topicId: string,
  subjectId: string,
  showSelfAssessments: 'self' | 'no_self',
) {
  const queryStr = getUrlFromObject({ ...filterObject, showSelfAssessments });
  switch (type) {
    case ReportEntityTypes.Company: {
      return fetchCompanyQuestionsImproveList(topicId, queryStr);
    }
    case ReportEntityTypes.User: {
      return fetchUserQuestionsImproveList(topicId, subjectId, queryStr);
    }
    case ReportEntityTypes.Group: {
      return fetchGroupQuestionsImproveList(topicId, subjectId, queryStr);
    }
    default:
      return null;
  }
}

export type FetchTopicListOfSubjectType = (
  type: ReportEntityTypes,
  filterObject: { [key: string]: string | null },
  subject: ReportsObj,
  me: SessionUserI,
  showSelfAssessments: 'self' | 'no_self',
) => ApiReduxResp<TopicsResponse>;

export function fetchQuestionListOfSubject(
  subjectType: ReportEntityTypes,
  filterObject: { [key: string]: string | null },
  topicId: string,
  subject: ReportsObj,
  me: SessionUserI,
  showSelfAssessments: 'self' | 'no_self',
) {
  const queryStr = getUrlFromObject({ ...filterObject, showSelfAssessments });
  switch (subjectType) {
    case ReportEntityTypes.Company: {
      return fetchCompanyQuestionsList(topicId, queryStr);
    }
    case ReportEntityTypes.User: {
      return fetchUserQuestionsList(topicId, subject.id, queryStr);
    }
    case ReportEntityTypes.Group: {
      return fetchGroupQuestionsList(topicId, subject.id, queryStr);
    }
    case ReportEntityTypes.Survey:
      return getSurveyQuestionsList(subject.id, topicId, queryStr, subject, me);

    default:
      return null;
  }
}

export type FetchQuestionListOfSubjectType = (
  subjectType: ReportEntityTypes,
  filterObject: { [key: string]: string | null },
  topicId: string,
  subject: ReportsObj,
  me: SessionUserI,
  showSelfAssessments: 'self' | 'no_self',
) => ApiReduxResp<QuestionResponse>;

export function fetchAnswerListOfSubject(
  subjectType: ReportEntityTypes,
  filterObject: { [key: string]: string | null },
  questionId: string,
  subject: ReportsObj,
  sessionUser: SessionUserI,
  showSelfAssessments: 'self' | 'no_self',
) {
  const queryStr = getUrlFromObject({ ...filterObject, showSelfAssessments });
  switch (subjectType) {
    case ReportEntityTypes.Company: {
      return fetchCompanyAnswersList(questionId, queryStr);
    }
    case ReportEntityTypes.User: {
      return fetchUserAnswersList(questionId, subject.id, queryStr);
    }
    case ReportEntityTypes.Group: {
      return fetchGroupAnswersList(questionId, subject.id, queryStr);
    }
    case ReportEntityTypes.Survey:
      return getSurveyQuestionAnswers(subject.id, questionId, subject, sessionUser, queryStr);
    default:
      return null;
  }
}

export type FetchAnswerListOfSubjectType = (
  subjectType: ReportEntityTypes,
  filterObject: { [key: string]: string | null },
  questionId: string,
  subject: ReportsObj,
  sessionUser: SessionUserI,
  showSelfAssessments: 'self' | 'no_self',
) => ApiReduxResp<AnswerResponse>;

export function fetchNewsListOfSubject(
  subjectType: ReportEntityTypes,
  subject: ReportsObj,
  me: SessionUserI,
  newsQueryObj?: { [key: string]: string | null },
) {
  const queryStr = queryString.stringify(newsQueryObj as InputParams);
  switch (subjectType) {
    case ReportEntityTypes.Company: {
      return fetchCompanyNewsList(queryStr);
    }
    case ReportEntityTypes.User: {
      return fetchUserNewsList(subject.id, queryStr);
    }
    case ReportEntityTypes.Group: {
      return fetchGroupNewsList(subject.id, queryStr);
    }
    case ReportEntityTypes.Survey:
      return getSurveyNews(subject.id, subject, me, queryStr);

    default:
      return null;
  }
}

/********************************************************************************
 *  DISTRIBUTERS FOR GETING THE ACTUAL DATA FROM STORE  *
 ********************************************************************************/

interface ReportsManagementObj {
  userProfile: UserProfileState;
  companyProfile: ProfileState;
  groupProfile: GroupProfileState;
  surveyProfile: SurveyProfileState;
}

export function getTopicsObject(
  subjectType: ReportEntityTypes,
  reportsManagementObj: ReportsManagementObj,
): TopicsResponse {
  const profileType = `${subjectType}Profile` as ProfileTypes;
  return reportsManagementObj[profileType].topics;
}

export function getNotesObject(
  subjectType: ReportEntityTypes,
  reportsManagementObj: ReportsManagementObj,
): NotesResponse {
  const profileType = `${subjectType}Profile` as ProfileTypes;
  return reportsManagementObj[profileType].notes;
}

export function getQuestionsObject(
  subjectType: ReportEntityTypes,
  reportsManagementObj: ReportsManagementObj,
): QuestionResponse {
  const profileType = `${subjectType}Profile` as ProfileTypes;
  return reportsManagementObj[profileType].questions;
}

export const selectSurveyOptions = (state: StoreI, {
  subjectType, from
}: { subjectType: ReportEntityTypes, from: 'questions' | 'answers' }): SortingOption[] => {
  const surveys = (from === 'questions' ? getQuestionsObject : getAnswersList)(subjectType, state.reportsManagement).result?.surveys ?? []

  const surveyOptions = surveys
    .map((survey: ProfileSurvey): SortingOption => {
      const label = translateContent(survey.name) + ` (${survey.count})`;
      return { label, name: survey.id, translationKey: label };
    })
    .sort((a, b) => (a.label && b.label && a.label > b.label ? 1 : -1))

  surveyOptions.unshift({ name: '', translationKey: 'x.reports.all_reports' });

  return surveyOptions;
}

export function getQuestionsImproveObject(
  subjectType: ReportEntityTypes,
  reportsManagementObj: ReportsManagementObj,
): ImproveResponse {
  const profileType = `${subjectType}Profile` as ProfileTypes;
  return reportsManagementObj[profileType].questions_improve;
}

export function getNewsData(
  subjectType: ReportEntityTypes,
  reportsManagementObj: ReportsManagementObj,
): NewsResponse {
  const profileType = `${subjectType}Profile` as ProfileTypes;
  return reportsManagementObj[profileType].news;
}

export function getAnswersList(
  subjectType: ReportEntityTypes,
  reportsManagementObj: ReportsManagementObj,
): AnswerResponse {
  const profileType = `${subjectType}Profile` as ProfileTypes;
  return reportsManagementObj[profileType].answers as AnswerResponse;
}

export function getListLoading(
  subjectType: ReportEntityTypes,
  reportsManagementObj: ReportsManagementObj,
): boolean {
  const profileType = `${subjectType}Profile` as ProfileTypes;
  return reportsManagementObj[profileType].loading;
}

/*******************************************************************************
 *  OTHER UTILS  *
 *******************************************************************************/

type AnyResponse = QuestionResponse | TopicsResponse | AnswerResponse | NewsResponse | ParticipantsResponse;

export function resultListToObject<R extends AnyResponse>(entity: ReportSubjectTypes, data: R) {
  const dataCopy = { ...data };
  if (dataCopy.result) {
    // @ts-ignore TS7017 TODO Maybe think of another solution because the BE provides the data only in this format
    dataCopy.result[`${entity}_list`] = mapIdToData(dataCopy.result[`${entity}_list`]);
  }
  return dataCopy;
}

export const defaultInitState: ProfileState = {
  loading: false,
  news: {
    options: null,
    result: null,
    pagination: null,
  },
  topics: {
    options: null,
    result: null,
    pagination: null,
  },
  notes: {
    items: [],
    pagination: null,
  },
  questions: {
    options: null,
    result: null,
    pagination: null,
  },
  questions_improve: {
    options: null,
    result: null,
    pagination: null,
  },
  answers: {
    options: null,
    result: null,
    pagination: null,
  },
};

export interface ProfileState {
  loading: boolean;
  news: NewsResponse;
  topics: TopicsResponse;
  questions: QuestionResponse;
  questions_improve: ImproveResponse;
  notes: NotesResponse;
  answers: {
    options: any;
    result: AnswerObj | null;
    pagination: PaginationI | null;
  };
}
